/**
 * Carry-forward helper - single source of truth for "is this lead
 * carrying over from a prior month?" so the list row pill, the
 * detail header pill, and any future tile share the same rule.
 *
 * Canonical definition (ADR-0042 + 2026-06-23 simplification):
 *
 *   A lead is a carry-forward when:
 *     - `createdAt < startOfCurrentMonth` (local time), AND
 *     - `currentBucket === 'OPEN'` (the operator is still working
 *       it - WON / LOST leads are terminal and never carry).
 *
 * The bucket check captures both `active` and `not_qualified`
 * (and any operator-added OPEN status) without us having to know
 * the canonical status codes here. Matches the `LeadsTrackerStrip`
 * carry-forward column's filter (`createdAtTo=prevMonthEnd +
 * bucket=OPEN`) so the row-level indicator and the strip count are
 * consistent.
 */

import { CARRYFORWARD_FLOOR_ISO } from '@utazzo/shared';

import { istIsoDate } from './lead-date-range';

interface CarryforwardCandidate {
  createdAt: string;
  currentBucket: string;
}

/**
 * Carry-forward floor - a FIXED cutover anchor, not a rolling window.
 *
 * The legacy CRM import carries ~13k OPEN leads reaching back to 2021. Under
 * the plain rule (OPEN + createdAt < this month) every one of them counts as
 * carry-forward, burying the real pipeline in pre-migration backlog. Leads
 * created before this date are therefore never carry-forward.
 *
 * Because the date is fixed rather than relative, the carry-forward rule
 * itself is unchanged for everything at or after it: a lead opened in June
 * still carries into July, August and beyond, and CF keeps accumulating month
 * over month exactly as designed. The floor only ever excludes the legacy
 * backlog, and stops mattering as the system ages past it.
 */
export { CARRYFORWARD_FLOOR_ISO };

// Server treats `createdAt` on the server side in UTC seconds; comparing
// against a browser-LOCAL month boundary would flip a lead that was
// created at IST 23:00 on the 31st into "carry-forward" for a UTC-locale
// browser (it sees the created_at as "next month" locally). Anchor to
// the IST month boundary so the pill/tile agree across time zones.
const carryforwardFloorMs = new Date(`${CARRYFORWARD_FLOOR_ISO}T00:00:00+05:30`).getTime();

/// Last day of the previous IST calendar month. Used as `createdAtTo`
/// when filtering the strict carry-forward slice - server will re-anchor
/// this to IST bounds via `istDayEndUtc`.
export function getPrevMonthEndIso(reference: Date = new Date()): string {
  // Move to the first of the current IST month, then step back one day.
  const [y, m] = istIsoDate(reference).split('-').map(Number);
  const firstOfThisIstMonthUtc = new Date(Date.UTC(y!, (m ?? 1) - 1, 1, 0, 0, 0));
  const prevDay = new Date(firstOfThisIstMonthUtc.getTime() - 24 * 60 * 60 * 1000);
  return istIsoDate(prevDay);
}

/// First-day-of-current-IST-month timestamp. Computed once per module
/// load - the inbox doesn't survive past midnight without a refresh and
/// any longer-running session can re-import.
const currentMonthStartMs = (() => {
  const [y, m] = istIsoDate().split('-').map(Number);
  // IST month start = `YYYY-MM-01T00:00:00+05:30`; drop 5:30h from UTC
  // midnight of the same date to get the true instant.
  const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
  return Date.UTC(y!, (m ?? 1) - 1, 1, 0, 0, 0) - IST_OFFSET_MS;
})();

export function isCarryforwardLead(lead: CarryforwardCandidate): boolean {
  if (lead.currentBucket !== 'OPEN') return false;
  const createdMs = new Date(lead.createdAt).getTime();
  if (createdMs < carryforwardFloorMs) return false; // pre-cutover legacy backlog
  return createdMs < currentMonthStartMs;
}
